/**
 * Tool marketplace types for Phase 2 & Phase 3 (Reviews)
 */

import type { ResourceProgressStatus } from "./models";

// =============================================================================
// Review Types
// =============================================================================

/**
 * Review status for moderation
 */
export type ReviewStatus = "pending" | "approved" | "rejected";

/**
 * Review create request
 */
export interface ReviewCreateRequest {
  rating: number; // 1-5
  reviewTitle?: string;
  reviewText?: string;
}

/**
 * Review update request
 */
export interface ReviewUpdateRequest {
  rating?: number;
  reviewTitle?: string;
  reviewText?: string;
}

/**
 * Public review response (for listing approved reviews)
 */
export interface ToolReview {
  id: string;
  toolId: string;
  rating: number;
  reviewTitle?: string | null;
  reviewText?: string | null;
  userName?: string | null;
  status: ReviewStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Current user's own review response (includes rejection reason)
 */
export interface MyReview extends ToolReview {
  rejectionReason?: string | null;
}

/**
 * Full review response for admin view with user details
 */
export interface ToolReviewWithUser extends ToolReview {
  userId: string;
  userEmail: string;
  tenantId: string;
  approvedAt?: string | null;
  approvedBy?: string | null;
  rejectionReason?: string | null;
}

/**
 * Paginated review list response
 */
export interface ReviewListResponse {
  reviews: ToolReview[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Admin paginated review list response
 */
export interface AdminReviewListResponse {
  reviews: ToolReviewWithUser[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Rating distribution (count per star rating)
 */
export interface RatingDistribution {
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
}

/**
 * Rating summary for a tool
 */
export interface RatingSummary {
  averageRating: number | null;
  ratingCount: number;
  ratingDistribution: RatingDistribution;
}

/**
 * Admin review moderation action
 */
export interface ReviewApprovalAction {
  action: "approve" | "reject";
  rejectionReason?: string;
}

/**
 * Pricing tier options for tools
 */
export type PricingTier = "free" | "freemium" | "paid" | "enterprise";

/**
 * Integration type options for tools
 */
export type IntegrationType =
  | "api"
  | "sdk"
  | "standalone"
  | "browser_extension"
  | "cli"
  | "saas";

/**
 * Tool category model
 */
export interface ToolCategory {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  displayOrder: number;
  isActive: boolean;
  toolCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Tool category (minimal for embedded display)
 */
export interface EmbeddedCategory {
  id: string;
  slug: string;
  name: string;
  icon?: string | null;
}

/**
 * Tool vendor model
 */
export interface ToolVendor {
  id: string;
  slug: string;
  name: string;
  website?: string | null;
  logoUrl?: string | null;
  description?: string | null;
  isVerified: boolean;
  isActive: boolean;
  displayOrder: number;
  toolCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Tool vendor (minimal for embedded display)
 */
export interface EmbeddedVendor {
  id: string;
  slug: string;
  name: string;
  logoUrl?: string | null;
  website?: string | null;
  isVerified: boolean;
}

/**
 * Tool model with marketplace metadata
 */
export interface Tool {
  id: string;
  title: string;
  description: string;
  externalUrl?: string | null;
  topics: string[];
  difficulty: string;
  author?: string | null;
  estimatedMinutes?: number | null;
  thumbnailUrl?: string | null;
  featured: boolean;
  createdAt: string;
  updatedAt: string;

  // Marketplace-specific fields
  category?: EmbeddedCategory | null;
  vendor?: EmbeddedVendor | null;
  pricingTier?: PricingTier | null;
  pricingDetails?: string | null;
  integrationType?: IntegrationType | null;
  documentationUrl?: string | null;
  githubUrl?: string | null;
  demoVideoUrl?: string | null;
  screenshotUrls?: string[] | null;
  viewCount: number;

  // Rating aggregates
  averageRating?: number | null;
  ratingCount: number;
}

/**
 * Tool with user-specific data
 */
export interface ToolWithUserData extends Tool {
  isBookmarked: boolean;
  progressStatus: ResourceProgressStatus | null;
}

/**
 * Tool query parameters
 */
export interface ToolQueryParams {
  search?: string;
  category?: string;
  vendor?: string;
  pricingTier?: PricingTier;
  integrationType?: IntegrationType;
  difficulty?: string;
  featured?: boolean;
  bookmarked?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * Paginated tool list response
 */
export interface ToolListResponse {
  tools: ToolWithUserData[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * View tracking response
 */
export interface ViewTrackResponse {
  success: boolean;
  viewCount: number;
}

/**
 * Category with tools response
 */
export interface CategoryWithToolsResponse {
  category: EmbeddedCategory & { description?: string | null };
  tools: Tool[];
  total: number;
}

/**
 * Vendor with tools response
 */
export interface VendorWithToolsResponse {
  vendor: EmbeddedVendor & {
    website?: string | null;
    description?: string | null;
  };
  tools: Tool[];
  total: number;
}

// =============================================================================
// API Response types (snake_case from backend, for transformation)
// =============================================================================

export interface ApiToolCategory {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  display_order: number;
  is_active: boolean;
  tool_count: number;
  created_at: string;
  updated_at: string;
}

export interface ApiToolVendor {
  id: string;
  slug: string;
  name: string;
  website?: string | null;
  logo_url?: string | null;
  description?: string | null;
  is_verified: boolean;
  is_active: boolean;
  display_order: number;
  tool_count: number;
  created_at: string;
  updated_at: string;
}

export interface ApiEmbeddedCategory {
  id: string;
  slug: string;
  name: string;
  icon?: string | null;
}

export interface ApiEmbeddedVendor {
  id: string;
  slug: string;
  name: string;
  logo_url?: string | null;
  is_verified: boolean;
}

export interface ApiTool {
  id: string;
  title: string;
  description: string;
  external_url?: string | null;
  topics: string[];
  difficulty: string;
  author?: string | null;
  estimated_minutes?: number | null;
  thumbnail_url?: string | null;
  featured: boolean;
  created_at: string;
  updated_at: string;
  category?: ApiEmbeddedCategory | null;
  vendor?: ApiEmbeddedVendor | null;
  pricing_tier?: PricingTier | null;
  pricing_details?: string | null;
  integration_type?: IntegrationType | null;
  documentation_url?: string | null;
  github_url?: string | null;
  demo_video_url?: string | null;
  screenshot_urls?: string[] | null;
  view_count: number;
  average_rating?: number | null;
  rating_count: number;
}

export interface ApiToolWithUserData extends ApiTool {
  is_bookmarked: boolean;
  progress_status: ResourceProgressStatus | null;
}

export interface ApiToolListResponse {
  tools: ApiToolWithUserData[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ApiViewTrackResponse {
  success: boolean;
  view_count: number;
}

// =============================================================================
// Transformation helpers
// =============================================================================

export function transformCategory(api: ApiToolCategory): ToolCategory {
  return {
    id: api.id,
    slug: api.slug,
    name: api.name,
    description: api.description,
    icon: api.icon,
    displayOrder: api.display_order,
    isActive: api.is_active,
    toolCount: api.tool_count,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function transformVendor(api: ApiToolVendor): ToolVendor {
  return {
    id: api.id,
    slug: api.slug,
    name: api.name,
    website: api.website,
    logoUrl: api.logo_url,
    description: api.description,
    isVerified: api.is_verified,
    isActive: api.is_active,
    displayOrder: api.display_order,
    toolCount: api.tool_count,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function transformEmbeddedCategory(
  api: ApiEmbeddedCategory | null | undefined,
): EmbeddedCategory | null {
  if (!api) return null;
  return {
    id: api.id,
    slug: api.slug,
    name: api.name,
    icon: api.icon,
  };
}

export function transformEmbeddedVendor(
  api: ApiEmbeddedVendor | null | undefined,
): EmbeddedVendor | null {
  if (!api) return null;
  return {
    id: api.id,
    slug: api.slug,
    name: api.name,
    logoUrl: api.logo_url,
    isVerified: api.is_verified,
  };
}

export function transformTool(api: ApiTool): Tool {
  return {
    id: api.id,
    title: api.title,
    description: api.description,
    externalUrl: api.external_url,
    topics: api.topics,
    difficulty: api.difficulty,
    author: api.author,
    estimatedMinutes: api.estimated_minutes,
    thumbnailUrl: api.thumbnail_url,
    featured: api.featured,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    category: transformEmbeddedCategory(api.category),
    vendor: transformEmbeddedVendor(api.vendor),
    pricingTier: api.pricing_tier,
    pricingDetails: api.pricing_details,
    integrationType: api.integration_type,
    documentationUrl: api.documentation_url,
    githubUrl: api.github_url,
    demoVideoUrl: api.demo_video_url,
    screenshotUrls: api.screenshot_urls,
    viewCount: api.view_count,
    averageRating: api.average_rating,
    ratingCount: api.rating_count,
  };
}

export function transformToolWithUserData(
  api: ApiToolWithUserData,
): ToolWithUserData {
  return {
    ...transformTool(api),
    isBookmarked: api.is_bookmarked,
    progressStatus: api.progress_status,
  };
}

export function transformToolListResponse(
  api: ApiToolListResponse,
): ToolListResponse {
  return {
    tools: api.tools.map(transformToolWithUserData),
    total: api.total,
    page: api.page,
    pageSize: api.page_size,
    totalPages: api.total_pages,
  };
}

// =============================================================================
// Review API Response types (snake_case from backend)
// =============================================================================

export interface ApiToolReview {
  id: string;
  tool_id: string;
  rating: number;
  review_title?: string | null;
  review_text?: string | null;
  user_name?: string | null;
  status: ReviewStatus;
  created_at: string;
  updated_at: string;
}

export interface ApiMyReview extends ApiToolReview {
  rejection_reason?: string | null;
}

export interface ApiToolReviewWithUser extends ApiToolReview {
  user_id: string;
  user_email: string;
  tenant_id: string;
  approved_at?: string | null;
  approved_by?: string | null;
  rejection_reason?: string | null;
}

export interface ApiReviewListResponse {
  reviews: ApiToolReview[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ApiAdminReviewListResponse {
  reviews: ApiToolReviewWithUser[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ApiRatingSummary {
  average_rating: number | null;
  rating_count: number;
  rating_distribution: Record<string, number>;
}

// =============================================================================
// Review Transformation helpers
// =============================================================================

export function transformReview(api: ApiToolReview): ToolReview {
  return {
    id: api.id,
    toolId: api.tool_id,
    rating: api.rating,
    reviewTitle: api.review_title,
    reviewText: api.review_text,
    userName: api.user_name,
    status: api.status,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function transformMyReview(api: ApiMyReview): MyReview {
  return {
    ...transformReview(api),
    rejectionReason: api.rejection_reason,
  };
}

export function transformReviewWithUser(
  api: ApiToolReviewWithUser,
): ToolReviewWithUser {
  return {
    ...transformReview(api),
    userId: api.user_id,
    userEmail: api.user_email,
    tenantId: api.tenant_id,
    approvedAt: api.approved_at,
    approvedBy: api.approved_by,
    rejectionReason: api.rejection_reason,
  };
}

export function transformReviewListResponse(
  api: ApiReviewListResponse,
): ReviewListResponse {
  return {
    reviews: api.reviews.map(transformReview),
    total: api.total,
    page: api.page,
    pageSize: api.page_size,
    totalPages: api.total_pages,
  };
}

export function transformAdminReviewListResponse(
  api: ApiAdminReviewListResponse,
): AdminReviewListResponse {
  return {
    reviews: api.reviews.map(transformReviewWithUser),
    total: api.total,
    page: api.page,
    pageSize: api.page_size,
    totalPages: api.total_pages,
  };
}

export function transformRatingSummary(api: ApiRatingSummary): RatingSummary {
  // Convert string keys to number keys for the distribution
  const distribution: RatingDistribution = {
    1: api.rating_distribution["1"] || 0,
    2: api.rating_distribution["2"] || 0,
    3: api.rating_distribution["3"] || 0,
    4: api.rating_distribution["4"] || 0,
    5: api.rating_distribution["5"] || 0,
  };

  return {
    averageRating: api.average_rating,
    ratingCount: api.rating_count,
    ratingDistribution: distribution,
  };
}
